/** RoyalEngagement — RSVP Section with background sitar music */

import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ExternalLink, Music } from 'lucide-react';
import { ROYAL_EASE } from '../theme';
import type { RoyalContent } from '../content';
import { useRsvp } from '@/src/modules/rsvp';

/** Simple inline form with validation – same as Dev21 but with subtle musical aura. */

const EMPTY_FORM = {
  name: '',
  email: '',
  attending: '' as 'yes' | 'no' | '',
  guests: '1',
  message: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form: typeof EMPTY_FORM) {
  const errors: Partial<Record<keyof typeof EMPTY_FORM, string>> = {};
  if (!form.name.trim()) errors.name = 'Please enter your name.';
  if (form.email && !EMAIL_RE.test(form.email.trim())) errors.email = 'Please enter a valid email address.';
  if (!form.attending) errors.attending = 'Let us know if you can make it.';
  if (form.attending === 'yes') {
    const n = Number(form.guests);
    if (!Number.isInteger(n) || n < 1 || n > 10) errors.guests = 'Guests must be a number between 1 and 10.';
  }
  return errors;
}

export function RsvpSection({
  content,
  invitationId,
  isLive = false,
}: {
  content: RoyalContent['rsvp'];
  invitationId?: string | null;
  isLive?: boolean;
}) {
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ ...EMPTY_FORM });
  const [errors, setErrors] = React.useState<Partial<Record<keyof typeof EMPTY_FORM, string>>>({});
  const audioRef = useRef<HTMLAudioElement>(null);

  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const submitted = state === 'success';
  const sending = state === 'loading';

  // Auto-play sitar loop – muted and looped for subtle ambience
  useEffect(() => {
    const audio = new Audio('/invitecloudFrontend/static/music/sitar_loop.mp3');
    audio.loop = true;
    audio.volume = 0.1;
    audioRef.current = audio;
    audio.play().catch(() => {});
    return () => {
      audio.pause();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    await submit({
      guestName: form.name.trim(),
      guestEmail: form.email.trim() || undefined,
      status: form.attending === 'yes' ? 'ACCEPTED' : 'DECLINED',
      headcount: form.attending === 'yes' ? Number(form.guests) : 1,
      message: form.message.trim() || undefined,
    });
  };

  return (
    <section id="rsvp" className="relative w-full px-5 py-20 sm:px-8 sm:py-24 lg:px-12 lg:py-32 bg-[var(--royal-ivory-dark)] text-[var(--royal-ink)]">
      <div className="mx-auto w-full max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: ROYAL_EASE }}
          className="flex flex-col items-center text-center"
        >
          <span className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-[var(--royal-maroon)]">
            <span className="h-px w-6 bg-[var(--royal-gold)]" />
            {content.label}
          </span>
          <h2 className="font-serif text-[clamp(2rem,5vw,3.4rem)] font-semibold leading-[1.08] text-[var(--royal-ink)]">
            {content.title}
          </h2>
          <p className="mt-4 max-w-2xl text-base text-[var(--royal-muted)]">
            {content.description}
          </p>
        </motion.div>

        <div className="mt-12 mx-auto w-full max-w-xl">
          {/* External link takes precedence */}
          {content.externalLink ? (
            <div className="rounded-3xl border border-[var(--royal-border)] bg-[var(--royal-ivory)] p-8 text-center backdrop-blur-md">
              <a
                href={content.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--royal-gold)] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-[var(--royal-maroon)] transition hover:-translate-y-0.5 hover:bg-[var(--royal-maroon)] hover:text-white"
              >
                <ExternalLink size={16} />
                Open RSVP Form
              </a>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {submitted ? (
                // Success card with musical note
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: ROYAL_EASE }}
                  className="rounded-3xl border border-[var(--royal-border)] bg-[var(--royal-ivory)] p-10 text-center backdrop-blur-md"
                >
                  <motion.span
                    initial={{ scale: 0, rotate: -45 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.6, delay: 0.1, ease: ROYAL_EASE }}
                    className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[var(--royal-gold)] text-white"
                  >
                    <Check size={36} />
                  </motion.span>
                  {/* Animated musical note */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6, ease: ROYAL_EASE }}
                    className="mt-4 flex items-center justify-center gap-2"
                  >
                    <Music size={24} className="text-[var(--royal-gold)]" />
                    <span className="font-serif text-lg text-[var(--royal-ink)]">
                      Thank you, {form.name.split(' ')[0]}!
                    </span>
                  </motion.div>
                  <p className="mt-2 text-[var(--royal-ink)]/65">
                    {form.attending === 'yes'
                      ? "We can't wait to celebrate with you."
                      : "We'll miss you — thank you for letting us know."}
                  </p>
                </motion.div>
              ) : (
                !showForm ? (
                  // Gate card
                  <motion.div
                    key="gate"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.5, ease: ROYAL_EASE }}
                    className="rounded-3xl border border-[var(--royal-border)] bg-[var(--royal-ivory)]/10 p-10 text-center backdrop-blur-md"
                  >
                    <p className="text-5xl mb-4">✨</p>
                    <h3 className="font-serif text-2xl font-semibold text-[var(--royal-ink)] mb-2">
                      Will You Join Us?
                    </h3>
                    <p className="text-[var(--royal-ink)]/55 text-sm mb-8">
                      Let us know if we can count on your presence.
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                      type="button"
                      onClick={() => setShowForm(true)}
                      className="inline-flex items-center gap-2 rounded-full bg-[var(--royal-gold)] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-[var(--royal-maroon)] transition hover:-translate-y-0.5 hover:bg-[var(--royal-maroon)] hover:text-white"
                    >
                      RSVP Now
                    </motion.button>
                  </motion.div>
                ) : (
                  // Form card
                  <motion.form
                    key="form"
                    noValidate
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: ROYAL_EASE }}
                    className="flex flex-col gap-5 rounded-3xl border border-[var(--royal-border)] bg-[var(--royal-ivory)] p-7 backdrop-blur-md sm:p-9"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex flex-col gap-1.5 text-left">
                        <span className="text-sm font-medium text-[var(--royal-ink)]">Full name</span>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Your name"
                          className={`w-full rounded-xl border bg-[var(--royal-ivory)] px-4 py-3 text-[var(--royal-ink)] outline-none transition focus:border-[var(--royal-gold)] focus:ring-2 focus:ring-[var(--royal-gold)]/20 ${errors.name ? 'border-red-500' : 'border-[var(--royal-border)]'}`}
                        />
                        {errors.name && <span className="text-xs font-medium text-red-600">{errors.name}</span>}
                      </label>
                      <label className="flex flex-col gap-1.5 text-left">
                        <span className="text-sm font-medium text-[var(--royal-ink)]">Email (optional)</span>
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="you@example.com"
                          className={`w-full rounded-xl border bg-[var(--royal-ivory)] px-4 py-3 text-[var(--royal-ink)] outline-none transition focus:border-[var(--royal-gold)] focus:ring-2 focus:ring-[var(--royal-gold)]/20 ${errors.email ? 'border-red-500' : 'border-[var(--royal-border)]'}`}
                        />
                        {errors.email && <span className="text-xs font-medium text-red-600">{errors.email}</span>}
                      </label>
                    </div>
                    <label className="flex flex-col gap-1.5 text-left">
                      <span className="text-sm font-medium text-[var(--royal-ink)]">Will you be attending?</span>
                      <div className="grid grid-cols-2 gap-3">
                        {(['yes', 'no'] as const).map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setForm({ ...form, attending: opt })}
                            className={`rounded-xl border px-4 py-2 text-sm font-medium capitalize transition ${form.attending === opt ? 'border-[var(--royal-gold)] bg-[var(--royal-gold)] text-white' : 'border-[var(--royal-border)] bg-[var(--royal-ivory)] text-[var(--royal-ink)] hover:border-[var(--royal-gold)]'}`}
                          >
                            {opt === 'yes' ? 'Joyfully accepts' : 'Regretfully declines'}
                          </button>
                        ))}
                      </div>
                      {errors.attending && <span className="text-xs font-medium text-red-600">{errors.attending}</span>}
                    </label>
                    {form.attending === 'yes' && (
                      <label className="flex flex-col gap-1.5 text-left">
                        <span className="text-sm font-medium text-[var(--royal-ink)]">Number of guests</span>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={form.guests}
                          onChange={(e) => setForm({ ...form, guests: e.target.value })}
                          className={`w-full rounded-xl border bg-[var(--royal-ivory)] px-4 py-3 text-[var(--royal-ink)] outline-none transition focus:border-[var(--royal-gold)] focus:ring-2 focus:ring-[var(--royal-gold)]/20 ${errors.guests ? 'border-red-500' : 'border-[var(--royal-border)]'}`}
                        />
                        {errors.guests && <span className="text-xs font-medium text-red-600">{errors.guests}</span>}
                      </label>
                    )}
                    <label className="flex flex-col gap-1.5 text-left">
                      <span className="text-sm font-medium text-[var(--royal-ink)]">A note for the couple (optional)</span>
                      <textarea
                        rows={3}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Send your wishes…"
                        className={`w-full rounded-xl border bg-[var(--royal-ivory)] px-4 py-3 text-[var(--royal-ink)] outline-none transition focus:border-[var(--royal-gold)] focus:ring-2 focus:ring-[var(--royal-gold)]/20 resize-none`}
                      />
                    </label>
                    {state === 'error' && errorMessage && (
                      <p className="text-center text-sm font-medium text-red-600">{errorMessage}</p>
                    )}
                    <button
                      type="submit"
                      disabled={sending}
                      className="mt-1 rounded-full bg-[var(--royal-gold)] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-[var(--royal-maroon)] transition hover:-translate-y-0.5 hover:bg-[var(--royal-maroon)] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {sending ? 'Sending…' : 'Send RSVP'}
                    </button>
                  </motion.form>
                )
              )}
            </AnimatePresence>
          )}
        </div>
      </div>
    </section>
  );
}
